import { useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { MdOutlineSettings } from "react-icons/md";

import OverlayTooltip from "../../../OverlayTooltip";

import DialogModal from "../../../DialogModal";
import { Input } from "../../../Utility/FormElements";
import {
  useIsFetchingGlobalReturnPermissions,
  useGetGlobalReturnPermissionsCacheData,
  useCreateGlobalReturnPermissions,
  useUpdateGlobalReturnPermissions,
} from "../returnPermission.util";

import { cn } from "../../../../helpers/cn";

const formID = "global-return-setting-form";

const globalReturnKeys = {
  returnBySelection: "Return by selection",
  returnByApproval: "Return by approval",
  returnNotAllowed: "Return not allowed",
};

const globalReturnOptions = Object.entries(globalReturnKeys).map(
  ([key, value]) => ({
    label: value,
    value: key,
  })
);

const getDefaultGlobalReturnSettingValue = (data) => {
  if (data === null || (data && !data?.isDisabled && !data?.isByApproval)) {
    return globalReturnKeys.returnBySelection;
  }

  if (data?.isDisabled) {
    return globalReturnKeys.returnNotAllowed;
  }

  if (data?.isByApproval) {
    return globalReturnKeys.returnByApproval;
  }

  return "";
};

function GlobalSettings() {
  const isLoading = useIsFetchingGlobalReturnPermissions();
  const currentGlobalReturnPermission =
    useGetGlobalReturnPermissionsCacheData();
  /**
   * @type {React.MutableRefObject<{show: function():void, hide: function():void} | null>} dialogModalRef
   */

  const dialogModalRef = useRef(null);

  const { mutate: mutateCreate } = useCreateGlobalReturnPermissions();
  const { mutate: mutateUpdate } = useUpdateGlobalReturnPermissions();

  const {
    register,
    setValue,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: {
      globalReturnSettingValue: "",
      reason: "",
    },
  });

  useEffect(() => {
    if (currentGlobalReturnPermission) {
      setValue(
        "globalReturnSettingValue",
        getDefaultGlobalReturnSettingValue(currentGlobalReturnPermission)
      );
    }
  }, [currentGlobalReturnPermission, setValue]);

  const onReset = () => {
    reset({
      globalReturnSettingValue: getDefaultGlobalReturnSettingValue(
        currentGlobalReturnPermission
      ),
      reason: "",
    });
  };

  const onCancel = () => {
    onReset();
    dialogModalRef.current.hide();
  };

  /**
   * @param {{globalReturnSettingValue: string, reason: string}} submitData
   */

  const onSubmit = (submitData) => {
    const formatData = {
      isDisabled:
        submitData.globalReturnSettingValue ===
        globalReturnKeys.returnNotAllowed,
      isByApproval:
        submitData.globalReturnSettingValue ===
        globalReturnKeys.returnByApproval,
      Reason: submitData.reason,
    };

    const isValidSubmission = () => {
      if (submitData.globalReturnSettingValue === "") {
        return false;
      }

      if (
        formatData?.isByApproval &&
        currentGlobalReturnPermission?.isByApproval
      ) {
        return false;
      }

      if (formatData?.isDisabled && currentGlobalReturnPermission?.isDisabled) {
        return false;
      }

      if (
        !formatData?.isByApproval &&
        !formatData?.isDisabled &&
        !currentGlobalReturnPermission?.isDisabled &&
        !currentGlobalReturnPermission?.isByApproval
      ) {
        return false;
      }

      return true;
    };

    if (!isValidSubmission()) {
      onReset();
      dialogModalRef.current?.hide();
      return;
    }

    // Create new global return permission if currentGlobalReturnPermission is null
    if (currentGlobalReturnPermission === null) {
      mutateCreate(formatData);
    } else {
      mutateUpdate(formatData);
    }

    dialogModalRef.current?.hide();
  };

  return (
    <div>
      <OverlayTooltip
        label={isLoading ? "Loading..." : "Global Return Settings"}
      >
        <button
          className={cn(
            "btn tw-border-0 tw-outline-none tw-grid tw-place-content-center hover:tw-bg-gray-200 tw-px-2",
            isLoading ? "tw-cursor-not-allowed tw-bg-gray-200" : ""
          )}
          type="button"
          onClick={() => dialogModalRef.current?.show()}
          disabled={isLoading}
        >
          <MdOutlineSettings className="tw-h-6 tw-w-6" />
        </button>
      </OverlayTooltip>

      <DialogModal
        ref={dialogModalRef}
        title="Global Return Setting"
        cancelButtonProps={{ onClick: onCancel }}
        saveButtonProps={{ type: "submit", form: formID, onClick: undefined }}
      >
        <form
          id={formID}
          className="tw-space-y-2"
          onSubmit={handleSubmit(onSubmit)}
        >
          <p
            className={`tw-text-base tw-mb-0 ${
              errors.globalReturnSettingValue ? "tw-text-red-500" : ""
            }`}
          >
            Global Return Setting*
          </p>

          <select
            className={`form-control ${
              errors?.globalReturnSettingValue
                ? "tw-border-red-500 focus:tw-border-red-500"
                : ""
            }`}
            {...register("globalReturnSettingValue", { required: true })}
          >
            <option value="" disabled>
              Select Global Return Setting *
            </option>
            {globalReturnOptions.map((option) => (
              <option key={option.value} value={option.label}>
                {option.label}
              </option>
            ))}
          </select>
          {errors.globalReturnSettingValue && (
            <small className="tw-text-red-500">Required</small>
          )}

          <Input
            id="reason"
            label="Reason*"
            {...register("reason", {
              required: true,
              validate: (value) => value.trim() !== "",
            })}
            isError={errors.reason}
            helperText={errors.reason && "Required"}
          />
        </form>
      </DialogModal>
    </div>
  );
}

export default GlobalSettings;
